import { Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmButton } from '@spartan-ng/helm/button';
import { SceneThumbnailComponent } from '../scene-thumbnail/scene-thumbnail.component';
import { PaginationComponent } from '../pagination/pagination.component';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';
import type { TaskScene } from '@web-portal/domains/shared/domain-models';

@Component({
  selector: 'ocp-scene-list',
  imports: [
    TranslatePipe,
    HlmButton,
    SceneThumbnailComponent,
    PaginationComponent,
    ThemedIconComponent,
  ],
  templateUrl: './scene-list.component.html',
  styleUrl: './scene-list.component.scss',
  host: {
    class: 'scene-list',
  },
})
export class SceneListComponent {
  /** List of scenes to display. */
  readonly scenes = input.required<TaskScene[]>();

  /** Zero-based index of the currently active scene. */
  readonly activeIndex = input(0);

  /** Emits when the user navigates to a different scene. */
  readonly activeIndexChange = output<number>();

  /** Emits when the user requests to delete the current scene. */
  readonly deleteScene = output<number>();

  /** Emits when the user requests to update the current scene. */
  readonly updateScene = output<number>();

  /** Emits when the user requests to open the current scene in model viewer. */
  readonly viewInModel = output<void>();

  /** Emits when the user requests to open the current scene. */
  readonly sceneClick = output<void>();

  /** Whether the update scene button should be visible. */
  readonly canUpdate = input(false);

  /** Whether any scene operation is pending (including host callback wait and API processing). */
  readonly sceneOperationPending = input(false);

  /** Whether the delete scene button should be visible. */
  readonly canDelete = input(false);

  /** Whether the view in model button should be visible. */
  readonly canViewInModel = input(false);

  protected readonly currentIndex = computed(() => this.activeIndex());

  protected readonly totalScenes = computed(() => this.scenes().length);

  protected readonly currentScene = computed<TaskScene | null>(() => {
    const list = this.scenes();
    const idx = this.currentIndex();
    return list[idx] ?? null;
  });

  protected readonly selectedObjectsCount = computed(
    () => this.currentScene()?.objects?.selected?.length ?? 0
  );

  protected onDeleteScene(event: Event): void {
    event.stopPropagation();

    if (this.sceneOperationPending()) {
      return;
    }

    this.deleteScene.emit(this.currentIndex());
  }

  protected onUpdateScene(event: Event): void {
    event.stopPropagation();
    this.updateScene.emit(this.currentIndex());
  }

  protected onSceneClick(event: Event): void {
    event.stopPropagation();
    this.sceneClick.emit();
  }

  protected onSceneKeyDown(event: KeyboardEvent): void {
    // Ignore key events coming from nested interactive elements (buttons, etc.).
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    if (event.key === ' ') {
      event.preventDefault();
    }

    this.onSceneClick(event);
  }

  protected onViewInModel(event: Event): void {
    event.stopPropagation();
    this.viewInModel.emit();
  }
}
